import { useCallback, useRef } from 'react';
import { telemetryManager } from '@repo/runtime-kit/utils/telemetry-manager';
import { useDeferredCleanupEffect } from './use-deferred-cleanup-effect';

interface UseActionSpanArgs {
  attributes?: Record<string, unknown>;
}

interface UseActionSpanReturn {
  markSuccess: () => void;
  markError: (error: unknown) => void;
}

export function useActionSpan(name: string, args: UseActionSpanArgs = {}): UseActionSpanReturn {
  const { attributes } = args;
  const endedRef = useRef(false);
  const attributesRef = useRef(attributes);
  attributesRef.current = attributes;

  useDeferredCleanupEffect(
    () => {
      telemetryManager.startUiAction(name);
    },
    () => {
      if (endedRef.current) return;
      telemetryManager.endUiAction(name, { attributes: attributesRef.current });
      endedRef.current = true;
    }
  );

  const markSuccess = useCallback(() => {
    if (endedRef.current) return;
    telemetryManager.endUiAction(name, { status: 'ok', attributes: attributesRef.current });
    endedRef.current = true;
  }, [name]);

  const markError = useCallback(
    (error: unknown) => {
      if (endedRef.current) return;
      telemetryManager.endUiAction(name, {
        status: 'error',
        exception: error,
        attributes: attributesRef.current,
      });
      endedRef.current = true;
    },
    [name]
  );

  return { markSuccess, markError };
}
